import * as React from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const REVIEW_BATCH_API_DEFAULT_ROW_THRESHOLD = 100;

interface Props {
  open: boolean;
  columnName: string;
  rowCount: number;
  isSubmitting: boolean;
  onConfirm: (executionMode: "sync" | "batch") => void;
  onOpenChange: (open: boolean) => void;
}

export const ReviewRunConfirmDialog: React.FC<Props> = ({
  open,
  columnName,
  rowCount,
  isSubmitting,
  onConfirm,
  onOpenChange,
}) => {
  const [useBatchApi, setUseBatchApi] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setUseBatchApi(rowCount >= REVIEW_BATCH_API_DEFAULT_ROW_THRESHOLD);
    }
  }, [open, rowCount]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Review responses: {columnName}</DialogTitle>
          <DialogDescription>
            Pathfinder will run this prompt again for every document in the collection, and an AI reviewer will compare
            each new response with the current one. Cells are then marked as Material difference, Inconclusive, or No
            material difference. Existing responses are not changed.
          </DialogDescription>
        </DialogHeader>

        <label className="my-1 flex items-start gap-2">
          <Checkbox
            checked={useBatchApi}
            onCheckedChange={checked => setUseBatchApi(checked === true)}
            className="mt-0.5"
          />
          <span className="text-sm">
            Use Batch API
            <span className="block text-xs text-muted-foreground">
              Cheaper for large collections. Results can take up to 24 hours.
            </span>
          </span>
        </label>

        <DialogFooter className="mt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={() => onConfirm(useBatchApi ? "batch" : "sync")}>
            {isSubmitting ? "Starting…" : "Start review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
